/**
 * Companies Slice
 * Manages company state and async operations
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { companyApi } from '../../utils/api/index.js';
import { extractPaginatedList, normalizePagination } from '../../utils/pagination.js';
import { getErrorMessage } from '../../utils/api/errors.js';

const normalizeCompanyRecord = (payload) => {
  if (!payload || typeof payload !== 'object') return payload;
  if (payload.data && typeof payload.data === 'object' && payload.data.id != null) {
    return payload.data;
  }
  return payload;
};

/**
 * Initial state
 */
const initialState = {
  companies: [],
  currentCompany: null,
  stats: null,
  loading: false,
  error: null,
  filters: {
    page: 1,
    limit: 20,
    status: null,
    search: '',
    plan: null,
    createdFrom: '',
    createdTo: '',
    dateRange: 'all',
    sortBy: 'created_at',
    sortOrder: 'desc',
  },
  pagination: {
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  },
};

/**
 * Async thunk: Fetch all companies
 */
export const fetchCompanies = createAsyncThunk(
  'companies/fetchAll',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await companyApi.getAll(params);
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch companies'));
    }
  }
);

/**
 * Async thunk: Fetch company by ID
 */
export const fetchCompanyById = createAsyncThunk(
  'companies/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await companyApi.getById(id);
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch company'));
    }
  }
);

/**
 * Async thunk: Create company
 */
export const createCompany = createAsyncThunk(
  'companies/create',
  async (companyData, { rejectWithValue }) => {
    try {
      const response = await companyApi.create(companyData);
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to create company'));
    }
  }
);

/**
 * Async thunk: Update company
 */
export const updateCompany = createAsyncThunk(
  'companies/update',
  async ({ id, companyData }, { rejectWithValue }) => {
    try {
      const response = await companyApi.update(id, companyData);
      return response;
    } catch (error) {
      const msg =
        (typeof error?.getUserMessage === 'function' && error.getUserMessage()) ||
        error.message ||
        'Failed to update company';
      return rejectWithValue(msg);
    }
  }
);

/**
 * Async thunk: Delete company
 */
export const deleteCompany = createAsyncThunk(
  'companies/delete',
  async (id, { rejectWithValue }) => {
    try {
      await companyApi.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to delete company'));
    }
  }
);

/**
 * Async thunk: Fetch company statistics
 */
export const fetchCompanyStats = createAsyncThunk(
  'companies/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await companyApi.getStats();
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch statistics'));
    }
  }
);

/**
 * Companies slice
 */
const companiesSlice = createSlice({
  name: 'companies',
  initialState,
  reducers: {
    /**
     * Clear current company
     */
    clearCurrentCompany: (state) => {
      state.currentCompany = null;
      state.error = null;
    },

    /**
     * Clear error
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Set filters
     */
    setFilters: (state, action) => {
      const payload = action.payload;
      const keys = Object.keys(payload);
      const shouldResetPage = keys.some((key) => key !== 'page') && !('page' in payload);
      state.filters = {
        ...state.filters,
        ...payload,
        ...(shouldResetPage ? { page: 1 } : {}),
      };
    },

    /**
     * Reset filters
     */
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },

    /**
     * Set pagination
     */
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all companies
      .addCase(fetchCompanies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCompanies.fulfilled, (state, action) => {
        state.loading = false;
        const { data, pagination } = extractPaginatedList(action.payload);
        state.companies = data;
        if (pagination) {
          state.pagination = normalizePagination(pagination, state.pagination);
        }
      })
      .addCase(fetchCompanies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.companies = [];
      })

      // Fetch company by ID
      .addCase(fetchCompanyById.pending, (state, action) => {
        const requestedId = Number(action.meta.arg);
        const isBackgroundRefresh =
          state.currentCompany && Number(state.currentCompany.id) === requestedId;
        if (!isBackgroundRefresh) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchCompanyById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCompany = normalizeCompanyRecord(action.payload);
      })
      .addCase(fetchCompanyById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.currentCompany = null;
      })

      // Create company
      .addCase(createCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCompany.fulfilled, (state, action) => {
        state.loading = false;
        state.companies.unshift(action.payload); // Add to beginning
      })
      .addCase(createCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update company (do not toggle global `loading` — avoids list/detail full-page spinners)
      .addCase(updateCompany.pending, (state) => {
        state.error = null;
      })
      .addCase(updateCompany.fulfilled, (state, action) => {
        const updated = normalizeCompanyRecord(action.payload);
        if (!updated?.id) return;
        const index = state.companies.findIndex((c) => c.id === updated.id);
        if (index !== -1) {
          state.companies[index] = updated;
        }
        if (state.currentCompany?.id === updated.id) {
          state.currentCompany = updated;
        }
      })
      .addCase(updateCompany.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Delete company
      .addCase(deleteCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCompany.fulfilled, (state, action) => {
        state.loading = false;
        state.companies = state.companies.filter((c) => c.id !== action.payload);
        if (state.currentCompany?.id === action.payload) {
          state.currentCompany = null;
        }
      })
      .addCase(deleteCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch statistics
      .addCase(fetchCompanyStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCompanyStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
      })
      .addCase(fetchCompanyStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearCurrentCompany,
  clearError,
  setFilters,
  resetFilters,
  setPagination,
} = companiesSlice.actions;

export default companiesSlice.reducer;
